import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { percentage, scoreLabel, trustLevel } from '../../Utils/trust';
import { useI18n } from '../../i18n/I18nProvider';

const TrustPassport = ({ profile = {}, compact = false, role }) => {
  const { language, t } = useI18n();
  const safeProfile = profile || {};
  const trust = safeProfile.trust || {};
  const normalizedRole = String(role || safeProfile.user_type || '').toLowerCase();
  const isClient = normalizedRole === 'client';
  const score = Number(safeProfile.trust_score ?? trust.score) || 0;
  const projects = Number(safeProfile.verified_projects ?? trust.verified_projects) || 0;
  const level = trustLevel(score, projects, language);
  const [open, setOpen] = useState(false);

  const metrics = useMemo(() => {
    if (isClient) {
      return [
        { icon: <TaskAltOutlinedIcon />, value: projects, label: t('trust.verifiedJobs') },
        { icon: <PaymentsOutlinedIcon />, value: percentage(trust.payment_reliability), label: t('trust.reliablePayments'), samples: trust.payment_samples },
        { icon: <ReplyOutlinedIcon />, value: percentage(trust.response_rate), label: t('trust.responsiveness'), samples: trust.response_samples },
      ];
    }
    return [
      { icon: <TaskAltOutlinedIcon />, value: projects, label: t('trust.verifiedJobs') },
      { icon: <ScheduleOutlinedIcon />, value: percentage(trust.on_time_rate), label: t('trust.punctuality'), samples: trust.on_time_samples },
      { icon: <ReplyOutlinedIcon />, value: percentage(trust.response_rate), label: t('trust.responsiveness'), samples: trust.response_samples },
    ];
  }, [isClient, projects, t, trust]);

  return (
    <Card $compact={compact}>
      <Top $compact={compact}>
        <Score $compact={compact}><strong>{score || '—'}</strong><span>/100</span></Score>
        <LevelCopy>
          <Badge $tone={level.tone}><VerifiedOutlinedIcon /> {level.label}</Badge>
          <h3>{scoreLabel(score, language)}</h3>
        </LevelCopy>
        <InfoButton type="button" aria-label={t('trust.understandScore')} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          <InfoOutlinedIcon />
        </InfoButton>
      </Top>
      <Stats $compact={compact}>
        {metrics.map((metric) => (
          <Stat key={metric.label} $compact={compact}>
            {metric.icon}
            <StatCopy>
              <strong>{metric.value}</strong>
              <span>{metric.label}</span>
              {metric.samples === 0 && <small>{t('trust.toMeasure')}</small>}
            </StatCopy>
          </Stat>
        ))}
      </Stats>
      {open && (
        <Explanation>
          <strong>{t('trust.explanationTitle')}</strong>
          <p>{t('trust.explanationBase')}</p>
          <p>{isClient ? t('trust.explanationClient') : t('trust.explanationArtisan')}</p>
          <p>{t('trust.explanationWeights')}</p>
        </Explanation>
      )}
      {!compact && !open && <Foot>{t('trust.missingMetrics')}</Foot>}
    </Card>
  );
};

const Card = styled.section`
  min-width: 0; display: grid; gap: ${({ $compact }) => ($compact ? '.75rem' : '1rem')};
  padding: ${({ $compact }) => ($compact ? '.85rem' : '1.1rem')}; overflow: hidden;
  border: 1px solid var(--color-border); border-radius: 1rem; background: #fff;
`;
const Top = styled.div`
  min-width: 0; display: grid;
  grid-template-columns: ${({ $compact }) => ($compact ? '3.65rem minmax(0, 1fr) auto' : '4.2rem minmax(0, 1fr) auto')};
  align-items: center; gap: ${({ $compact }) => ($compact ? '.7rem' : '.9rem')};
`;
const LevelCopy = styled.div`min-width:0;h3{margin:.35rem 0 0;font-size:.92rem;line-height:1.25;overflow-wrap:anywhere;}`;
const Score = styled.div`
  width:${({$compact})=>$compact?'3.65rem':'4.2rem'};height:${({$compact})=>$compact?'3.65rem':'4.2rem'};display:grid;place-content:center;border-radius:1rem;background:#111;color:#fff;text-align:center;
  strong{font-size:${({$compact})=>$compact?'1.2rem':'1.45rem'};line-height:1;}span{font-size:.62rem;color:#d7c5a4;}
`;
const Badge = styled.span`
  max-width:100%;display:inline-flex;align-items:center;gap:.3rem;padding:.32rem .52rem;border-radius:999px;
  background:${({$tone})=>$tone==='expert'?'#111':$tone==='trusted'?'#e8f7ee':$tone==='verified'?'#f5ead7':'#f1ece5'};
  color:${({$tone})=>$tone==='expert'?'#fff':$tone==='trusted'?'#17623a':'#6c541c'};font-size:.66rem;font-weight:850;line-height:1.25;white-space:normal;
  svg{flex:0 0 auto;font-size:.9rem;}
`;
const InfoButton = styled.button`
  width:2rem;height:2rem;display:grid;place-items:center;border:1px solid #ded7cd;border-radius:50%;background:#faf8f5;color:#4d4943;cursor:pointer;
  svg{font-size:1rem;} &:hover{border-color:#c0a375;background:#fff8ec;}
`;
const Stats = styled.div`min-width:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:${({$compact})=>$compact?'.38rem':'.5rem'};@media(max-width:30rem){grid-template-columns:1fr;}`;
const Stat = styled.div`
  min-width:0;display:${({$compact})=>$compact?'grid':'flex'};grid-template-rows:${({$compact})=>$compact?'auto auto':'none'};justify-items:${({$compact})=>$compact?'center':'initial'};align-items:center;align-content:center;gap:${({$compact})=>$compact?'.3rem':'.45rem'};padding:${({$compact})=>$compact?'.62rem .28rem':'.65rem'};border-radius:.8rem;background:#f7f5f1;color:#5d574e;text-align:${({$compact})=>$compact?'center':'left'};
  >svg{flex:0 0 auto;font-size:${({$compact})=>$compact?'1.05rem':'1rem'};}
`;
const StatCopy = styled.div`
  min-width:0;display:grid;justify-items:inherit;gap:.05rem;
  strong{color:#111;font-size:.82rem;line-height:1.1;white-space:nowrap;}span{min-width:0;font-size:.62rem;line-height:1.2;overflow-wrap:anywhere;hyphens:auto;}small{color:#8a8175;font-size:.55rem;}
`;
const Explanation = styled.div`
  padding:.8rem;border:1px solid #e4d9c8;border-radius:.8rem;background:#fffaf1;color:#4f483e;
  strong{font-size:.76rem;}p{margin:.35rem 0 0;font-size:.68rem;line-height:1.5;}
`;
const Foot = styled.p`margin:0;color:var(--color-textMuted);font-size:.7rem;line-height:1.45;`;

export default TrustPassport;
